<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <!--table hauptkonfigurationen -->
                <v-infinite-scroll @load="load">
                    <v-data-table-server
                        v-model:sort-by="sortBy"
                        v-model="selected"
                        :headers="headers"
                        :items="dokumentList"
                        :loading="loading"
                        :items-length="totalNumberOfRecords || 0"
                        hide-default-footer
                        class="elevation-1"
                        :must-sort="true"
                        :custom-sort="customSort"
                        show-select
                        hover
                        return-object
                        @update:sort-by="onSortByChanged"
                    >
                        <!-- Header der Table(Filter anzeige, gefundene Elemente) -->
                        <template #top>
                            <TableHeader v-if="selected.length < 1" :filters="filters" @chip-closed="removeFilter">
                                <template #default>
                                    <div class="table-header-item-count">
                                        {{ totalNumberOfRecords + ' ' + $t('archiv.table.header.items') }}
                                    </div>
                                    <v-text-field
                                        v-if="showSearchBar"
                                        v-model="filterData.bezeichnung"
                                        class="ml-5"
                                        prepend-inner-icon="mdi-magnify"
                                        append-icon="mdi-close"
                                        :label="$t('archiv.table.header.searchlabel')"
                                        hide-details
                                        autofocus
                                        @update:model-value="searchFieldInputChanged"
                                        @click:append="searchBarClosed"
                                    ></v-text-field>
                                    <v-spacer />
                                    <v-tooltip top color="secondary">
                                        <template #activator="{ props }">
                                            <v-btn v-bind="props" icon color="primary">
                                                <v-icon v-if="!showSearchBar" @click="showSearchBar = !showSearchBar">mdi-magnify</v-icon>
                                            </v-btn>
                                        </template>
                                        <div>
                                            <span>
                                                {{ $t('tableheader.tooltip.search') }}
                                            </span>
                                        </div>
                                    </v-tooltip>
                                    <v-tooltip top color="secondary">
                                        <template #activator="{ props }">
                                            <v-btn v-bind="props" icon color="primary" @click="downloadAllDokumenteWithFilter">
                                                <v-icon>mdi-download</v-icon>
                                            </v-btn>
                                        </template>
                                        <div>
                                            <span>
                                                {{ $t('tableheader.tooltip.download') }}
                                            </span>
                                        </div>
                                    </v-tooltip>
                                    <v-tooltip top color="secondary">
                                        <template #activator="{ props }">
                                            <v-btn v-bind="props" icon color="primary" @click="showFilterDrawer = !showFilterDrawer">
                                                <v-icon>mdi-filter-outline</v-icon>
                                            </v-btn>
                                        </template>
                                        <div>
                                            <span>
                                                {{ $t('tableheader.tooltip.filter') }}
                                            </span>
                                        </div>
                                    </v-tooltip>
                                </template>
                            </TableHeader>
                            <TableHeader v-else :filters="filters" :download-toolbar="true" @chip-closed="removeFilter">
                                <template #default>
                                    <div>
                                        {{ selected.length + ' ' + $t('archiv.table.header.items') + ' (total ' + maximumFileSize + ')' }}
                                    </div>
                                    <v-spacer />
                                    <div class="pr-3">
                                        <a style="color: white" @click="downloadMultipleDokumenteWrapper">
                                            Download<v-icon color="white">mdi-download</v-icon>
                                        </a>
                                        <span class="pl-1 pr-1" style="font-size: 20px">|</span>
                                        <a style="color: white" @click="selected = []"> Cancel </a>
                                    </div>
                                </template>
                            </TableHeader>
                        </template>
                        <!-- Selbst definierte Tablespalten -->
                        <template #item.bezeichnung="{ item }">
                            <a style="text-decoration: underline">
                                <TextHighlights
                                    :text="item.bezeichnung"
                                    :search-text="filterData.bezeichnung"
                                    @click="
                                        openDokument({
                                            id: item.dokumentGuid,
                                            dateiTyp: item.mimeTyp,
                                            dokumentName: item.bezeichnung
                                        })
                                    "
                                />
                            </a>
                        </template>
                        <template #item.schlagworte="{ item }">
                            <TextHighlights :text="item.schlagworte" :search-text="filterData.bezeichnung" />
                        </template>
                        <template #item.dokumentTypCode="{ item }">
                            <span>
                                {{ $t(item.dokumentTypCode) }}
                            </span>
                        </template>
                        <template #item.link="{ item }">
                            <v-btn
                                icon
                                class="elevation-0"
                                :href="item.link"
                                @click="
                                    downloadDokument({
                                        id: item.dokumentGuid,
                                        dokumentName: item.bezeichnung
                                    })
                                "
                            >
                                <v-icon color="primary" icon="mdi-download-outline" />
                            </v-btn>
                        </template>
                        <template #item.burgermenu="{ item }">
                            <v-menu v-if="hasAccess('Archiv.EigeneDokumente.Schreiben')" v-model="item.burgermenuActive" offset-x>
                                <template #activator="{ props }">
                                    <v-btn icon :href="item.burgermenu" v-bind="props" class="elevation-0">
                                        <v-icon color="primary" icon="mdi-menu" />
                                    </v-btn>
                                </template>
                                <v-list>
                                    <EditDokumentDialog
                                        :dokument-id="item.dokumentID"
                                        :full-dokument-name="item.dateiName + '.' + item.dateiErweiterung"
                                        :dokument-typ="item.dokumentTypCode"
                                    />
                                    <ConfirmDialog
                                        v-if="item.dokumentTypCode == 'archiv.dokumenttyp.eigenesdok'"
                                        title="archiv.deletedialog.title"
                                        text="archiv.deletedialog.text"
                                        @confirm="deleteDokumentWrapper(item.dokumentID)"
                                    >
                                        <template #activatorElement="{ props }">
                                            <v-list-item v-bind="props">
                                                <v-list-item-title v-text="$t('archiv.table.burgermenu.delete')" />
                                            </v-list-item>
                                        </template>
                                    </ConfirmDialog>
                                    <v-list-item
                                        @click="
                                            downloadDokument({
                                                id: item.dokumentGuid,
                                                dokumentName: item.bezeichnung
                                            })
                                        "
                                    >
                                        <v-list-item-title v-text="$t('archiv.table.burgermenu.download')" />
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                            <v-btn v-else icon disabled>
                                <v-icon color="grey" icon="mdi-menu" />
                            </v-btn>
                        </template>
                    </v-data-table-server>
                </v-infinite-scroll>
            </v-col>
        </v-row>
        <!-- FilterMenü -->
        <FilterDrawer
            v-model:visible="showFilterDrawer"
            @cancel="cancelFilter()"
            @confirm="confirmFilter()"
            @clear="clearFilters(true)"
            @reset="clearFilters(false)"
        >
            <template #default>
                <SingleFilter :title="$t('archiv.table.header.dokumenttyp')">
                    <template #default>
                        <v-row>
                            <v-col cols="12">
                                <v-select v-model="filterData.dokumentTyp" variant="filled" density="compact" hide-details :items="dokumentTypes"></v-select>
                            </v-col>
                        </v-row>
                    </template>
                </SingleFilter>
                <SingleFilter :title="$t('archiv.table.header.filetyp')">
                    <template #default>
                        <v-row>
                            <v-col scols="12">
                                <v-select v-model="filterData.fileTyp" variant="filled" density="compact" hide-details :items="fileTypes"></v-select>
                            </v-col>
                        </v-row>
                    </template>
                </SingleFilter>
                <SingleFilter :title="$t('archiv.table.header.erstelltvon')">
                    <template #default>
                        <v-row>
                            <v-col cols="12">
                                <v-select v-model="filterData.erstelltVon" variant="filled" density="compact" hide-details :items="erstelltVon"></v-select>
                            </v-col>
                        </v-row>
                    </template>
                </SingleFilter>
                <SingleFilter title="" class="py-0 mt-0">
                    <template #default>
                        <v-radio-group v-model="radios" class="pb-0 filterLine" inline>
                            <h5 class="pb-3">{{ $t('archiv.filter.title.datum') }}</h5>
                            <v-col cols="12" class="pt-0">
                                <v-radio value="selectTime" :label="$t('archiv.filter.datum.radio.selecttime')"></v-radio>
                            </v-col>
                            <v-col cols="12" class="pt-0">
                                <v-radio value="allTime" :label="$t('archiv.filter.datum.radio.alltime')"></v-radio>
                            </v-col>
                            <v-col cols="12" class="pt-0">
                                <v-radio value="specificTime" :label="$t('archiv.filter.datum.radio.specifictime')"></v-radio>
                            </v-col>
                            <v-col cols="12" class="pb-0 pt-0">
                                <v-row>
                                    <v-col class="pb-0" cols="6">
                                        <NowhowDatepicker v-model="filterData.dateFrom" :disabled="radios !== 'specificTime'" label="archiv.filter.datum.von">
                                        </NowhowDatepicker>
                                    </v-col>
                                    <v-col class="pb-0" cols="6">
                                        <NowhowDatepicker v-model="filterData.dateTo" :disabled="radios !== 'specificTime'" label="archiv.filter.datum.bis">
                                        </NowhowDatepicker>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-radio-group>
                    </template>
                </SingleFilter>
            </template>
        </FilterDrawer>
    </v-container>
</template>

<script lang="ts">
import { Component, Watch, mixins } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import debounce from 'lodash/debounce';
import { DateMixin } from '@/mixins/DateMixin';

import { NAME as NAME_GENERAL } from '@/store/modules/general';
import { NAME as NAME_ARCHIV } from '../store';
import { NAME as NAME_SECURITY } from '@/store/modules/security';
import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
import { NAME as NAME_RESOURCE } from '@/store/modules/resource';

const generalModul = namespace(NAME_GENERAL);
const archivModul = namespace(NAME_ARCHIV);
const securityModul = namespace(NAME_SECURITY);
const headerModul = namespace(NAME_HEADERNOTIFICATION);
const resourceModul = namespace(NAME_RESOURCE);

import { GET_LANGUAGE } from '@/store/modules/resource/getters';
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';
import { NEW_ALERT } from '@/store/modules/headerNotification/actions';
import {
    GET_ALL_DOKUMENTE,
    GET_ALL_DOKUMENTTYPES,
    GET_ALL_FILETYPES,
    DOWNLOAD_DOKUMENT,
    OPEN_DOKUMENT,
    DELETE_DOKUMENT,
    DOWNLOAD_MULTIPLE_DOKUMENTE,
    DOWNLOAD_ALL_DOKUMENTE,
    GET_ALL_ERSTELLTVON
} from '../store/actions';
import { GETDOKUMENTE, GETDOKUMENTTYPES, GETFILETYPES, LOADING, TOTAL_NUMBER_OF_RECORDS, GETERSTELLTVON } from '../store/getters';
import { CLEARFILTER, APPLYFILTER } from '../store/mutations';
import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';

import SingleFilter from '@/components/SingleFilter.vue';
import FilterDrawer from '@/components/FilterDrawer.vue';
import TableHeader from '@/components/TableHeader.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import EditDokumentDialog from './EditDokumentDialog.vue';
import NowhowDatepicker from '@/components/NowhowDatepicker.vue';
import TextHighlights from '@/components/TextHighlight/TextHighlights.vue';
import { TableHeaderFilterItem } from '@/types/TableHeaderFilterItem';
import { DokumentApiDto } from '@/api-schema';
import { DokumentFilterDto } from '@/types/DokumentFilterDto';
@Component({
    components: {
        SingleFilter,
        FilterDrawer,
        TableHeader,
        ConfirmDialog,
        EditDokumentDialog,
        NowhowDatepicker,
        TextHighlights
    }
})
export default class Archiv extends mixins(DateMixin) {
    @archivModul.Action(GET_ALL_DOKUMENTE) getFilteredDokumente!: Function;
    @archivModul.Action(GET_ALL_DOKUMENTTYPES) getDokumentTypes!: Function;
    @archivModul.Action(GET_ALL_FILETYPES) getFileTypes!: Function;
    @archivModul.Action(GET_ALL_ERSTELLTVON) getErstelltVon!: Function;
    @archivModul.Action(DOWNLOAD_DOKUMENT) downloadDokument!: Function;
    @archivModul.Action(DOWNLOAD_ALL_DOKUMENTE) downloadAllDokumente!: Function;
    @archivModul.Action(DOWNLOAD_MULTIPLE_DOKUMENTE)
    downloadMultipleDokumente!: Function;
    @archivModul.Action(OPEN_DOKUMENT) openDokument!: Function;
    @archivModul.Action(DELETE_DOKUMENT) deleteDokument!: Function;

    @archivModul.Getter(GETDOKUMENTE) dokumentList!: Array<DokumentApiDto>;
    @archivModul.Getter(GETDOKUMENTTYPES) dokumentTypes!: Array<object>;
    @archivModul.Getter(GETFILETYPES) fileTypes!: Array<object>;
    @archivModul.Getter(GETERSTELLTVON) erstelltVon!: Array<object>;
    @archivModul.Getter(LOADING) loading!: boolean;
    @archivModul.Getter(TOTAL_NUMBER_OF_RECORDS) totalNumberOfRecords!: number;

    @archivModul.Mutation(CLEARFILTER) clearFilter!: Function;
    @archivModul.Mutation(APPLYFILTER) applyFilters!: Function;

    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;
    @resourceModul.Getter(GET_LANGUAGE) language!: string;

    @headerModul.Action(NEW_ALERT) alertMessage!: Function;

    date = '';

    burgermenuActive = false;
    dialog = false;
    showSearchBar = false;
    searchField = '';
    showFilterDrawer = false;
    filters: Array<TableHeaderFilterItem> = [];
    filterData: DokumentFilterDto = new DokumentFilterDto();
    filterDataCopy: DokumentFilterDto = new DokumentFilterDto();
    timeFilter = 360;
    radios = 'selectTime';
    radiosCopy = '';
    sortBy: [{ key: 'createdOn'; order: 'desc' }];
    //Liste der selektierten Dokumente
    selected: Array<DokumentApiDto> = [];
    maximumFileSize = '-';
    infiniteLoad = null;

    get headers(): Array<any> {
        return [
            {
                title: this.$t('archiv.table.header.datum'),
                key: 'createdOn'
            },
            {
                title: this.$t('archiv.table.header.dokumentname'),
                key: 'bezeichnung'
            },
            {
                title: this.$t('archiv.table.header.schlagworte'),
                key: 'schlagworte'
            },
            {
                title: this.$t('archiv.table.header.createdby'),
                key: 'erstelltVon'
            },
            {
                title: this.$t('archiv.table.header.dokumenttyp'),
                key: 'dokumentTypCode'
            },
            {
                title: this.$t('archiv.table.header.filetyp'),
                width: '95',
                key: 'dateiErweiterung'
            },
            {
                title: this.$t('archiv.table.header.download'),
                key: 'link',
                align: 'center',
                width: '10',
                sortable: false
            },
            {
                title: '',
                value: 'burgermenu',
                align: 'center',
                width: '10',
                sortable: false
            }
        ];
    }

    mounted() {
        if (!this.hasAccess('Navigation.Dokumentarchiv')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
            this.filterData.zeitspanne = 'Last365Days';
            this.filters.push({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: this.$t('archiv.filter.chip.datum.selecttime').toString()
            });
            this.applyFilters(this.filterData);
            this.getFilteredDokumente(0);
            this.getDokumentTypes();
            this.getFileTypes();
            this.getErstelltVon();
        }
        this.searchFieldInputChanged = debounce(() => {
            this.filterData.schlagworte = this.filterData.bezeichnung;
            this.applyFilters(this.filterData);
            this.getFilteredDokumente(0);
        }, 400);
    }

    /* Funktion deklarieren, aber erst in mount mit debounce initialisieren */
    searchFieldInputChanged!: () => void;

    created() {
        this.setSubTitle(this.$t('archiv.title'));
        this.setFooterTitle('S300');
    }

    private clearFilters(closeFilter: boolean) {
        this.radios = 'selectTime';
        this.filterData = new DokumentFilterDto();
        this.filterData.zeitspanne = 'Last365Days';
        if (closeFilter) {
            this.clearFilter();
            this.filters = [];
            this.applyFilters(this.filterData);
            this.showFilterDrawer = false;
        }
    }

    private searchBarClosed() {
        this.showSearchBar = false;
        this.filterData.bezeichnung = undefined;
        this.filterData.schlagworte = undefined;
        this.applyFilters(this.filterData);
        this.getFilteredDokumente(0);
    }

    private deleteDokumentWrapper(dokumentID: number) {
        this.deleteDokument(dokumentID).then(() => {
            this.selected = [];
        });
    }

    private removeFilter(value: string) {
        this.filters = this.filters.filter(function (obj) {
            return obj.key !== value;
        });
        if (value === 'archiv.filter.chip.title.datum') {
            this.filterData.dateTo = undefined;
            this.filterData.dateFrom = undefined;
            this.filterData.zeitspanne = undefined;
            this.radios = 'allTime';
        } else if (value === 'archiv.table.header.dokumenttyp') {
            this.filterData.dokumentTyp = '';
        } else if (value === 'archiv.table.header.filetyp') {
            this.filterData.fileTyp = '';
        } else if (value === 'archiv.table.header.erstelltvon') {
            this.filterData.erstelltVon = '';
        }
        this.applyFilters(this.filterData);
        this.getFilteredDokumente(0);
    }

    private ShowActiveFilters() {
        this.filters = [];
        if (this.radios === 'specificTime') {
            this.filterData.zeitspanne = undefined;
            let filterValue = '';
            if (this.filterData.dateFrom && this.filterData.dateTo) {
                filterValue = this.filterData.dateFrom + ' - ' + this.filterData.dateTo;
            } else if (this.filterData.dateFrom) {
                filterValue = '<= ' + this.filterData.dateFrom;
            } else if (this.filterData.dateTo) {
                filterValue = '>= ' + this.filterData.dateTo;
            }

            this.filters = this.filters.concat({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: filterValue
            });
        }
        if (this.radios === 'allTime') {
            this.filterData.dateTo = undefined;
            this.filterData.dateFrom = undefined;
            this.filterData.zeitspanne = undefined;
        }
        if (this.radios === 'selectTime') {
            this.filterData.dateTo = undefined;
            this.filterData.dateFrom = undefined;
            this.filterData.zeitspanne = 'Last365Days';
            this.filters = this.filters.concat({
                key: 'archiv.filter.chip.title.datum',
                nameResourceKey: 'archiv.filter.chip.title.datum',
                value: this.$t('archiv.filter.chip.datum.selecttime').toString()
            });
        }
        if (this.filterData.dokumentTyp !== '') {
            this.filters = this.filters.concat({
                key: 'archiv.table.header.dokumenttyp',
                nameResourceKey: 'archiv.table.header.dokumenttyp',
                value: this.$t(this.filterData.dokumentTyp).toString()
            });
        }
        if (this.filterData.fileTyp !== '') {
            this.filters = this.filters.concat({
                key: 'archiv.table.header.filetyp',
                nameResourceKey: 'archiv.table.header.filetyp',
                value: this.$t(this.filterData.fileTyp).toString()
            });
        }
        if (this.filterData.erstelltVon !== '') {
            this.filters = this.filters.concat({
                key: 'archiv.table.header.erstelltvon',
                nameResourceKey: 'archiv.table.header.erstelltvon',
                value: this.$t(this.filterData.erstelltVon).toString()
            });
        }
    }

    private confirmFilter() {
        this.ShowActiveFilters();
        this.applyFilters(this.filterData);
        this.getFilteredDokumente(0);
        this.showFilterDrawer = false;
    }

    private cancelFilter() {
        this.filterData = Object.assign({}, this.filterDataCopy);
        this.radios = this.radiosCopy;
        this.showFilterDrawer = false;
    }

    @Watch('showFilterDrawer')
    onFilterDrawerOpened() {
        if (this.showFilterDrawer) {
            this.getDokumentTypes();
            this.getFileTypes();
            this.getErstelltVon();
            this.filterDataCopy = Object.assign({}, this.filterData);
            this.radiosCopy = this.radios;
        }
    }

    private customSort(items: any[]) {
        return items;
    }

    onSortByChanged(newSortBy: any) {
        this.sortBy = newSortBy;
        if (this.sortBy.length > 0) {
            const sortField = this.sortBy[0]; // { key: 'lieferantName', order: 'desc' }

            this.filterData.sortBy = sortField.key.charAt(0).toUpperCase() + sortField.key.slice(1);
            this.filterData.sortOrder = sortField.order === 'desc' ? 'Descending' : 'Ascending';
        } else {
            this.filterData.sortBy = null;
            this.filterData.sortOrder = null;
        }
        this.applyFilters(this.filterData);
        this.getFilteredDokumente(0);
    }

    @Watch('language')
    onLanugageChanged() {
        this.ShowActiveFilters();
    }

    @Watch('selected')
    onSelectedFilesChanged() {
        let combinedFileSize = 0;
        this.selected.forEach((element) => {
            combinedFileSize += element.groesseInBytes;
        });

        if (combinedFileSize > 0) {
            let i = -1;
            const byteUnits = [' kB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB'];
            do {
                combinedFileSize = combinedFileSize / 1024;
                i++;
            } while (combinedFileSize > 1024);

            this.maximumFileSize = Math.max(combinedFileSize, 0.1).toFixed(1) + byteUnits[i];
        } else {
            this.maximumFileSize = '-';
        }
    }

    async load({ done }) {
        if (!this.infiniteLoad) {
            this.infiniteLoad = done;
        }
        if (this.totalNumberOfRecords === null || this.totalNumberOfRecords === undefined) {
            // initialer datenload abwarten, sonst wird fälschlicherweise 'empty' emitted
            done('ok');
            return;
        }
        await this.getFilteredDokumente(this.dokumentList.length);

        if (this.dokumentList.length >= this.totalNumberOfRecords) {
            done('empty');
            return;
        }
        done('ok');
    }
    downloadMultipleDokumenteWrapper() {
        const selectedDokumenteGuids = this.selected.map((dokument) => dokument.dokumentGuid);
        this.downloadMultipleDokumente({ ids: selectedDokumenteGuids });
    }
    downloadAllDokumenteWithFilter() {
        this.applyFilters(this.filterData);
        this.downloadAllDokumente(this.totalNumberOfRecords);
    }

    @Watch('totalNumberOfRecords')
    onTotalNumberOfRecordsChanged() {
        if (this.infiniteLoad) {
            this.infiniteLoad('ok');
        }
    }
}
</script>

<style scoped>
.v-application a {
    color: #0d47a1;
    cursor: pointer;
}
</style>
